import React from "react";

const Article = () => {
  return (
    <div className="mb-20">
      <div>
        <h2 id="engineering-overview_o">Engineering overview</h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          The aim of our project is to tackle cadmium ion bioaccumulation in the
          human body <a href="#reference1">
            <sup>1</sup>
          </a>{" "}. To meet our needs the engineered system must fulfill
          several requirements and constraints. The first requirement to be met
          is <strong>catching cadmium ions</strong>. This interaction should be{" "}
          <strong>highly specific</strong> to avoid depleting essential bivalent
          cations such as zinc, calcium, and magnesium. The solution is the
          synthetic phytochelatin EC20<a href="#reference2">
            <sup>2</sup>
          </a>{" "}.
        </p>
        <p className="mb-2">
          The second constraint is catching cadmium before it is absorbed{" "}
          <strong>in the small intestine</strong>. To bring our phytochelatin to
          its place of action we use a Live Biotherapeutic Product (LBP).
          Consequently, this implies bringing EC20{" "}
          <strong>to the surface of our LBP</strong>. To achieve that, we are
          using Lpp-OmpA, a well characterized surface display system in
          bacteria.
        </p>
        <p className="mb-2">
          Although the LBP must have limited capacities to divide in the small
          intestine to increase safety, it must{" "}
          <strong>stay long enough to mediate cadmium catching</strong>. To do
          so we are adding an anchor to our LBP, SpaC <a href="#reference3">
            <sup>3</sup>
          </a>{" "}, a protein able to bind to
          the intestinal mucus to moor our LBP to the intestinal lining.
        </p>
        <p className="mb-2">
          However, once bound to cadmium,{" "}
          <strong>the LBP needs to be expelled from the body</strong> in order
          to eliminate the cadmium. In this way, the production by the LBP of
          SpaC - the mucus anchor - needs to be stopped after the LBP production
          in laboratory. The solution is to couple an IPTG inducible Lac
          operator system with a theophylline riboswitch <a href="#reference4">
            <sup>4</sup>
          </a>{" "} to control
          transcription and translation of SpaC, respectively.
        </p>
        <p className="mb-2">
          Acting in the small intestine implies contact with the host
          microbiota. We want to{" "}
          <strong>limit the LBP - microbiota interaction</strong> as much as
          possible to avoid microbiota disruption. Our answer to this constraint
          is to impair horizontal gene transfer by deleting the Dut and Ung<a href="#reference5">
            <sup>5</sup>
          </a>{" "}
          genes in our chassis.
        </p>
        <br />
        <table className="mb-20">
          <tr>
            <th>Aim</th>
            <td>Tackling cadmium ion bioaccummulation in the human body</td>
          </tr>
          <tr>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <th>Requirement / Constraint</th>
            <th>Solution</th>
          </tr>
          <tr>
            <td>Catching cadmium ions specifically EC20</td>
            <td>EC20</td>
          </tr>
          <tr>
            <td>In the small intestine</td>
            <td>A Live Biotherapeutic product</td>
          </tr>
          <tr>
            <td>To the LBT surface Lpp-OmpA</td>
            <td>Lpp-OmpA</td>
          </tr>
          <tr>
            <td>Stay long enough to mediate cadmium catching</td>
            <td>SpaC</td>
          </tr>
          <tr>
            <td>The LBP needs to be expelled from the body</td>
            <td>
              Control of SpaC expression with an IPTG inducible Lac operator
              coupled with theophylline riboswitch
            </td>
          </tr>
          <tr>
            <td>Limit the LBP - microbiota interaction</td>
            <td>Dut & Ung double negative chassis</td>
          </tr>
        </table>
        <br />
        <figure className="flex flex-col justify-center items-center gap-4">
          <img
            style={{ width: "60%", height: "60%" }}
            alt="spac"
            id="figure-1-both"
            src="https://static.igem.wiki/teams/4888/wiki/engineering/both.png"
          />
          <figcaption>
            <b>
              <u>Figure 1:</u>{" "}
            </b>
            Bacteria overview
          </figcaption>
        </figure>
        <br />
        <h2 className="mt-20" id="engineering-overview-parts_o">
          Principal parts of interest
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <h3 className="mb-5 mt-10" id="overview-ec20_o">
          EC20
        </h3>
        <p className="mb-2">
          EC20 is a synthetic phytochelatin with an affinity for cadmium ions
          that is higher than for other bivalent cations such as zinc. First
          synthetized by W. Bae & al. 2000 (3), it is composed of 20 repetitions
          of the amino acids pair Glutamic acid plus Cysteine, and a terminal
          Glycine. We use its binding capacities to catch and trap cadmium ions
          at the surface of our live biotherapeutic product.
        </p>
        <h3 className="mb-5 mt-10" id="overview-spac_o">
          SpaC
        </h3>
        <p className="mb-2">
          SpaC is a subunit of the Lactobacillus rhamnosus GG sortase-dependent
          pili, SpaCBA. Its natural function is to adhere to human mucus and
          collagen<a href="#reference3">
            <sup>3,</sup>
          </a>{" "} <a href="#reference6">
            <sup>6</sup>
          </a>{" "}. We use its binding capacities to act as an anchor for
          our live biotherapeutic product.
        </p>
        <h3 className="mb-5 mt-10" id="overview-lpp-ompa_o">
          Lpp-OmpA
        </h3>
        <p className="mb-2">
          Lpp-OmpA is a well-known and characterized part of synthetic biology.
          It is a fusion protein of two E. coli proteins:
        </p>
        <br />
        <ul
          className="ml-12 mb-10"
          style={{ listStyleType: "disc", listStylePosition: "inside" }}
        >
          <li>
            The signal sequence and the first nine N-terminal amino acids of the
            major outer membrane lipoprotein, Lpp.
          </li>
          <li>
            The amino acids 46 to 159 of the outer membrane protein A, OmpA.
          </li>
        </ul>
        <br />
        <p className="mb-2">
          Localized to the bacterium outer membrane, this part serves as a
          surface display system for both EC20 and SpaC proteins when fused to
          the respective N terminus of the aformentioned proteins.
        </p>
        <h3 className="mb-5 mt-10" id="overview-IPTG">
          IPTG inducible Lac operator + LacI
        </h3>
        <p className="mb-2">
          The gene expression control system composed of the IPTG inducible Lac
          operator and the LacI inhibitor is a well-known and used transcription
          control system. In absence of IPTG, the inhibitor blocks RNA
          polymerase binding to the gene promotor and inhibits the gene
          transcription. We use this IPTG inducible Lac operator coupled with a
          Theophylline riboswitch to control SpaC expression and avoid
          expression leakiness.
        </p>
        <h3 className="mb-5 mt-10" id="overview-theophiline-riboswitch_o">
          Theophylline riboswitch
        </h3>
        <p className="mb-2">
          The Theophylline riboswitch is a synthetic riboswitch Engineered by
          S.Topp & al. 2010<a href="#reference4">
            <sup>4</sup>
          </a>{" "}. It regulates gene expression at the translation
          level via theophylline-RNA interactions, sequestering the ribosome
          binding site when Theophylline is absent. We use this riboswitch
          coupled to a IPTG inducible Lac operator to control SpaC expression
          and avoid expression leakiness.
        </p>
        <h3 className="mb-5 mt-10" id="overview-dut-ung_o">
          Dut and Ung genes
        </h3>
        <p className="mb-2">
          The DUT gene encodes a crucial enzyme involved in nucleotide
          metabolism. This enzyme plays a vital role in converting dUTP into
          dUMP and pyrophosphate. This enzymatic reaction serves two essential
          functions in the cell: it provides the necessary precursor (dUMP) for
          the synthesis of thymine nucleotides required for DNA replication and
          helps regulate the intracellular levels of dUTP. Elevated dUTP levels
          can result in the increased incorporation of uracil into DNA,
          triggering a complex excision repair process mediated by uracil
          glycosylase.
        </p>
        <p className="mb-2">
          The UNG gene plays an important role in encoding an enzyme called
          uracil-DNA glycosylase. One of its essential roles is to safeguard the
          integrity of DNA by removing uracil from DNA molecules.
        </p>
        <p className="mb-2">
          Negative mutants for those genes are used to impair Horizontal Gene
          Transfer between our Live Biotherapeutic Product and
          microbiota’s strains.
        </p>
        <h3 className="mb-5 mt-10" id="overview-imp">
          Testing implementation
        </h3>
        <p className="mb-2">
          For the proof of concept, we decided to test separately the cadmium
          catcher protein, EC20, the anchor mucus binding protein, SpaC, and the
          H.G.T impaired chassis, Dut/Ung double negative mutant. For both EC20
          and SpaC, we used the well characterized lab strain E. coli BL21 DE3
          as it is well suited for laboratory manipulations and experiments. For
          the Dut/Ung double negative mutant we used the competent E. coli
          strain CJ236, a double negative chassis for the genes of interest <a href="#reference7">
            <sup>7</sup>
          </a>{" "}.
        </p>
        <p className="mb-2">
          This choice was possible because their functions are independent. It
          was motivated by its simplicity as well as by the fact that those
          three pieces, if operational, could be used by others for their own
          biological machine.
        </p>

        <h2 className="mt-20" id="references_o">
          References
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="reference1">
              {" "}
              Rafati Rahimzadeh M, Rafati Rahimzadeh M, Kazemi S, Moghadamnia
              AA. <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5596182/">
                Cadmium toxicity and treatment: An update. Caspian J Intern Med.
                2017 Summer
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="reference2">
              {" "}
              Bae, Weon, et al. <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1002/1097-0290%2820001205%2970%3A5%3C518%3A%3AAID-BIT6%3E3.0.CO%3B2-5">
                Enhanced bioaccumulation of heavy metals by bacterial cells
                displaying synthetic phytochelatins.
              </a>{" "}
              <br />
              Biotechnology and bioengineering 70.5 (2000): 518-524{" "}
            </li>
            <li id="reference3">
              {" "}
              Kankainen, Matti, et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/19805152/">
                Comparative genomic analysis of Lactobacillus rhamnosus GG
                reveals pili containing a human-mucus binding protein.
              </a>{" "}
              <br />
              Proceedings of the National Academy of Sciences 106.40 (2009):
              17193-17198.{" "}
            </li>
            <li id="reference4">
              {" "}
              Topp, Shana et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/20935124/">
                Synthetic riboswitches that induce gene expression in diverse
                bacterial species.
              </a>{" "}
              <br />
              Applied and environmental microbiology vol. 76,23 (2010): 7881-4.
              doi:10.1128/AEM.01537-10{" "}
            </li>
            <li id="reference5">
              {" "}
              Algar, Elena, et al. <br />{" "}
              <a href="https://www.researchgate.net/publication/342531782_Environmental_Performance_of_Pseudomonas_putida_with_a_Uracylated_Genome">
                {" "}
                Environmental performance of Pseudomonas putida with a
                uracylated genome.{" "}
              </a>{" "}
              <br />
              ChemBioChem 21.22 (2020): 3255-3265.{" "}
            </li>
            <li id="reference6">
              {" "}
              Kant, Abhiruchi, et al. <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/32653644/">
                Crystal structure of lactobacillar SpaC reveals an atypical
                five-domain pilus tip adhesin: Exposing its substrate-binding
                and assembly in SpaCBA pili.
              </a>{" "}
              <br />
              Journal of Structural Biology 211.3 (2020): 107571{" "}
            </li>
            <li id="reference7">
              {" "}
              Lari, Sibghat-Ullah, et al.
              <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/16908222/">
                Quantitative determination of uracil residues in Escherichia
                coli DNA: Contribution of ung, dug, and dut genes to uracil
                avoidance.
              </a>{" "}
              <br />
              DNA repair 5.12 (2006): 1407-1420{" "}
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
